import { Button } from '@/client/components/ui/button';
import { AvatarStack } from '@/client/components/common/AvatarStack';
import { ProjectIcon } from './ProjectIcon';
import { StatusBadge } from './StatusBadge';
import type { RouterOutputs } from '@/client/utils/trpc';

type Project = RouterOutputs['projects']['list'][number];

interface ProjectsTableProps {
  projects: Project[];
  isLoading: boolean;
  isAdmin: boolean;
  onRowClick: (id: string) => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}

export function ProjectsTable({
  projects,
  isLoading,
  isAdmin,
  onRowClick,
  onEdit,
  onDelete,
}: ProjectsTableProps) {
  if (isLoading) {
    return <p className="px-5 py-4 text-sm text-muted-foreground">Loading…</p>;
  }
  if (projects.length === 0) {
    return <p className="px-5 py-4 text-sm text-muted-foreground">No projects found.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-muted-foreground">
          <th className="px-5 pb-2 pt-3 font-medium">Project</th>
          <th className="pb-2 pt-3 font-medium">Status</th>
          <th className="pb-2 pt-3 font-medium">Team</th>
          <th className="pb-2 pt-3 font-medium">Created</th>
          <th className="pb-2 pt-3" />
        </tr>
      </thead>
      <tbody className="divide-y">
        {projects.map((p) => (
          <tr
            key={p.id}
            className="cursor-pointer transition-colors hover:bg-muted/40"
            onClick={() => onRowClick(p.id)}
          >
            <td className="px-5 py-3">
              <div className="flex items-center gap-3">
                <ProjectIcon name={p.name} size={32} />
                <div>
                  <p className="font-medium">{p.name}</p>
                  {p.client_name && (
                    <p className="text-xs text-muted-foreground">{p.client_name}</p>
                  )}
                </div>
              </div>
            </td>
            <td className="py-3">
              <StatusBadge status={p.status} />
            </td>
            <td className="py-3">
              <AvatarStack names={p.members.map((m) => m.full_name ?? '?')} size={24} max={3} />
            </td>
            <td className="py-3 text-muted-foreground">
              {new Date(p.created_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </td>
            <td className="py-3 pr-5 text-right">
              {isAdmin && (
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(p);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(p);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
